<template>
  <PForm
    v-model="validado"
    @cancel="cancel"
    @accept="accept"
    :action="actionName"
    :textAction="textActionName"
    :textCancel="$t('label.cancelar')"
  >
    <template>
      <POptionBarNew :options="options" v-model="optionSelected" class="option-bar">
        <template #header>
          <PRow>
            <PCol>
              <PFormInputMultipleFile
                v-model="files"
                :label="`Fichero`"
                rules="required"
                accept=".doc, docx, .txt, .pdf, .xls, .xlsx, .csv, .zip"
                v-if="optionSelected === 'multiple'"
              />
              <PFormInputFile
                v-model="file"
                :label="`Fichero`"
                rules="required"
                accept=".doc, .docx, .txt, .pdf, .xls, .xlsx, .csv, .zip"
                v-else
              />
            </PCol>
          </PRow>
          <PRow class="mb-2">
            <PCol>
              <div class="fileFormat">
                <img
                  :id="`infoFileFormat`"
                  src="@/assets/icons/diferencia-icon.svg"
                  alt="InfoFileFormat"
                  class="icon-fileformat"
                />

                <b-tooltip
                  :target="`infoFileFormat`"
                  custom-class="table-cell-tooltip"
                  placement="right"
                >
                  <template v-if="optionSelected === 'multiple'">
                    {{ $t("label.fileFormat") }} <b>{{ $t("label.fileFormatExt") }}</b>
                    <br />
                  </template>
                  {{ $t("label.fileType") }} <b>{{ $t("label.fileTypeExt") }}</b>
                </b-tooltip>
              </div>
            </PCol>
          </PRow>
        </template>

        <template #individual>
          <PRow>
            <PCol col="6">
              <PFormSelect
                v-model="formData.pais"
                :label="$t('label.pais')"
                :options="paises"
                optionValue="codPaisCorto"
                optionText="descripcion"
                :rules="`required`"
                :placeholder="$t('option.select')"
                @change="onPaisSelectedChange"
              />
            </PCol>
          </PRow>
          <PRow>
            <PCol col="3">
              <PFormSelect
                v-model="formData.clienteId"
                :label="$t('label.cliente')"
                :options="clientesFiltered"
                optionValue="id"
                optionText="desCliente"
                :rules="`required`"
                :placeholder="$t('option.select')"
              />
            </PCol>
            <PCol col="3">
              <PFormSelect
                v-model="formData.delegacionId"
                :label="$t('label.delegacion')"
                :options="delegaciones"
                optionValue="id"
                optionText="desDelegacion"
                :rules="`required`"
                :placeholder="$t('option.select')"
              />
            </PCol>
            <PCol col="3">
              <PFormDatepicker
                v-model="formData.fechaDocumento"
                :label="$t('label.fecha')"
                :language="locale"
                :endSelector="today"
              />
            </PCol>
            <PCol col="3">
              <PFormSelect
                v-model="formData.tipoDocumento"
                :label="$t('label.tipoDocumento')"
                :options="documentTypes"
                optionValue="codParametro"
                optionText="desParametro"
                :rules="`required`"
                :placeholder="$t('option.select')"
              />
            </PCol>
          </PRow>
        </template>
      </POptionBarNew>
    </template>
  </PForm>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Multiselect from "vue-multiselect";
import { ValidationObserver, ValidationProvider } from "vee-validate";
import VueMethods from "@/vue-methods";
import {
  IOptionBarItem,
  PForm,
  PFormDatepicker,
  PFormInputFile,
  PFormSelect,
  POptionBarNew,
} from "@/components/look";
import { Getter } from "vuex-class";
import { Locale } from "@/store";
import { ICliente, IClienteAll, IDelegacion, IPais, IParametro } from "@/entities";
import { IDocumentacionImportacionIndividualCreateParams } from "@/services";

@Component({
  name: "importar-form",
  mixins: [VueMethods],
  components: {
    Multiselect,
    ValidationObserver,
    ValidationProvider,
    POptionBarNew,
    PFormSelect,
    PFormInputFile,
    PFormDatepicker,
    PForm,
  },
})
export default class ImportacionForm extends Vue {
  @Getter("getLocale") locale!: Locale;
  @Getter("getClientesAll") clientes!: IClienteAll[];
  @Getter("getPaises") paises!: IPais[];
  @Getter("getClienteSelected") clienteSelected!: ICliente | null;
  @Prop({ required: false }) actionName!: string;
  @Prop({ required: false }) textActionName!: string;

  files: File[] = [];
  file: File | null = null;
  optionSelected = "individual";
  validado = true;
  documentTypes: IParametro[] = [];

  formData: IDocumentacionImportacionIndividualCreateParams = {
    file: {} as File,
    pais: "",
    clienteId: "",
    delegacionId: "",
    tipoDocumento: "",
    fechaDocumento: new Date().toISOString(),
  };

  // lista clientes
  clientesFiltered: IClienteAll[] = [];
  // lista delegaciones
  delegaciones: IDelegacion[] = [];

  $refs!: {
    formImportar: InstanceType<typeof ValidationObserver>;
  };

  async beforeMount(): Promise<void> {
    await this.initializeForm();
  }

  async onPaisSelectedChange(newValue: string): Promise<void> {
    if (!newValue) {
      this.resetDependentFields();
      return;
    }

    await this.handleNewClientSelection(newValue);
  }

  @Watch("formData.clienteId")
  async onClientSelectedChange(newValue: string): Promise<void> {
    if (!newValue) {
      this.delegaciones = [];
      this.formData.delegacionId = "";
      return;
    }

    const cliente = await this.$services.clientes._fetchCliente(Number(newValue));
    this.delegaciones = this.fetchDelegacionesByCliente(cliente);
    this.formData.delegacionId = "";
  }

  @Watch("formData.fechaDocumento")
  onFechaAsociatedChange(newValue: string): void {
    const selectedDate = new Date(newValue);
    const today = new Date();
    if (selectedDate > today) {
      this.formData.fechaDocumento = today.toISOString();
    }
  }

  fetchDelegacionesByCliente(cliente: ICliente | undefined): IDelegacion[] {
    const delegaciones = cliente?.clientexdels?.map((clientexdel) => clientexdel.delegacion);
    return delegaciones || [];
  }

  filterClientesByPais(paisCod: string): IClienteAll[] {
    return this.clientes.filter((cliente) => cliente.pais?.codPaisCorto === paisCod);
  }

  async handleNewClientSelection(codPaisCorto: string): Promise<void> {
    this.clientesFiltered = this.filterClientesByPais(codPaisCorto);
    const paisId = this.paises.find((pais) => pais.codPaisCorto === codPaisCorto)?.id || 0;
    this.documentTypes =
      (await this.$services.documentacion.fetchTiposDocumentosByPais(paisId)) || [];
    this.delegaciones = [];
    this.formData.clienteId =
      this.clientesFiltered
        .find((cliente) => cliente.id?.toString() === this.formData.clienteId)
        ?.id?.toString() || "";
    this.formData.delegacionId = "";
  }

  async initializeForm(): Promise<void> {
    const clienteSelected = this.clienteSelected;
    const hasOnePais = this.paises.length == 1;

    if (clienteSelected) {
      this.formData.pais = clienteSelected.pais?.codPaisCorto || "";
      this.formData.clienteId = clienteSelected.id!.toString();
    } else if (hasOnePais) {
      this.formData.pais = this.paises[0]?.codPaisCorto || "";
    } else {
      this.resetDependentFields();
    }
  }

  resetDependentFields(): void {
    this.clientesFiltered = [];
    this.documentTypes = [];
    this.delegaciones = [];
    this.formData.clienteId = "";
    this.formData.delegacionId = "";
    this.formData.tipoDocumento = "";
  }

  async accept(action: string): Promise<void> {
    if (action == "importar") {
      if (this.optionSelected == "multiple") {
        await this.importFile();
      } else {
        await this.importFileIndividual();
      }
    }
  }

  async importFile(): Promise<void> {
    this.$emit("action", { action: "importacionConfirm", data: this.files });
  }

  async importFileIndividual(): Promise<void> {
    this.formData.file = this.file!;

    const fechaISO = new Date(this.formData.fechaDocumento).toISOString();
    const payload: IDocumentacionImportacionIndividualCreateParams = {
      ...this.formData,
      fechaDocumento: fechaISO,
    };

    this.$emit("action", {
      action: "importacionIndividualConfirm",
      data: payload,
    });
  }

  cancel(): void {
    this.$emit("cancel");
  }

  // Opciones para el componente POptionBarNew
  get options(): IOptionBarItem[] {
    return [
      {
        value: "individual",
        text: "Individual",
        hidden: false,
      },
      {
        value: "multiple",
        text: "Multiple",
        hidden: false,
      },
    ];
  }

  get today(): string {
    return new Date().toISOString().split("T")[0];
  }
}
</script>

<style lang="scss" scoped>
.form_ImportarForm {
  padding: 1rem;
}

.btnDownload {
  margin-top: 26px;
  float: right;
}

.icon-fileformat {
  width: 30px;
  height: 30px;
}

.option-bar::v-deep {
  & > .p-option-bar-container {
    padding-top: 2rem;
  }

  @media (min-width: 681px) {
    & > .p-option-bar-container {
      margin-top: -1em !important;
    }
  }
}
</style>
